import { create } from "zustand";
import type { ApiDilemma, ApiUploadedFile } from "../types";

const DEFAULT_ANSWERS = [
  { id: 1, answer: "", isCorrect: false, feedback: "" },
  { id: 2, answer: "", isCorrect: false, feedback: "" },
  { id: 3, answer: "", isCorrect: false, feedback: "" },
  { id: 4, answer: "", isCorrect: false, feedback: "" },
];

type Step1Data = {
  title: string;
  topic: string;
  description: string;
  default_language: "fr" | "en";
};

type Step2Answer = {
  id: number;
  answer: string;
  isCorrect: boolean;
  feedback: string;
};

type Step2Data = {
  question: string;
  answers: Step2Answer[];
};

type Step3Data = {
  expertSynthesis: string;
  documentationLinks: string[];
  uploadedFiles: ApiUploadedFile[];
};

type DilemmaFormState = {
  isEditMode: boolean;

  createdDilemma: ApiDilemma | null;
  setCreatedDilemma: (dilemma: ApiDilemma | null) => void;

  initialDilemma: ApiDilemma | null;

  currentStep: number;
  setCurrentStep: (step: number) => void;

  step1Data: Step1Data;
  setStep1Data: (data: Partial<Step1Data>) => void;

  step2Data: Step2Data;
  setStep2Data: (data: Partial<Step2Data>) => void;

  step3Data: Step3Data;
  setStep3Data: (data: Partial<Step3Data>) => void;

  reviewComment: string;
  setReviewComment: (value: string) => void;

  isStep1Dirty: boolean;
  isStep2Dirty: boolean;
  isStep3Dirty: boolean;
  setIsStep1Dirty: (dirty: boolean) => void;
  setIsStep2Dirty: (dirty: boolean) => void;
  setIsStep3Dirty: (dirty: boolean) => void;

  initFromApi: (dilemma: ApiDilemma) => void;
  reset: () => void;
};

const initialState = {
  isEditMode: false,
  createdDilemma: null,
  initialDilemma: null,
  currentStep: 1,
  step1Data: {
    title: "",
    topic: "",
    description: "",
    default_language: "fr" as const,
  },
  step2Data: {
    question: "",
    answers: DEFAULT_ANSWERS,
  },
  step3Data: {
    expertSynthesis: "",
    documentationLinks: [],
    uploadedFiles: [],
  },
  reviewComment: "",
  isStep1Dirty: false,
  isStep2Dirty: false,
  isStep3Dirty: false,
};

export const useDilemmasFormStore = create<DilemmaFormState>((set, get) => ({
  ...initialState,

  setCreatedDilemma: (dilemma) => set({ createdDilemma: dilemma }),
  setCurrentStep: (step) => set({ currentStep: step }),

  setStep1Data: (data) =>
    set((state) => ({ step1Data: { ...state.step1Data, ...data } })),

  setStep2Data: (data) =>
    set((state) => ({ step2Data: { ...state.step2Data, ...data } })),

  setStep3Data: (data) =>
    set((state) => ({ step3Data: { ...state.step3Data, ...data } })),

  setReviewComment: (value) => set({ reviewComment: value }),

  setIsStep1Dirty: (dirty) => {
    if (dirty && !get().isStep1Dirty) set({ isStep1Dirty: true });
  },
  setIsStep2Dirty: (dirty) => {
    if (dirty && !get().isStep2Dirty) set({ isStep2Dirty: true });
  },
  setIsStep3Dirty: (dirty) => {
    if (dirty && !get().isStep3Dirty) set({ isStep3Dirty: true });
  },

  initFromApi: (dilemma) => {
    const question = dilemma.questions[0];
    set({
      isEditMode: true,
      createdDilemma: dilemma,
      initialDilemma: dilemma,
      currentStep: 1,
      step1Data: {
        title: dilemma.name,
        topic: dilemma.topic.id,
        description: dilemma.description,
        default_language: dilemma.default_language ?? dilemma.source_language,
      },
      step2Data: {
        question: question?.payload ?? "",
        answers: question
          ? question.answers.map((a, i) => ({
              id: i + 1,
              answer: a.payload,
              isCorrect: a.is_correct,
              feedback: a.comment,
            }))
          : DEFAULT_ANSWERS.map((a) => ({ ...a })),
      },
      step3Data: {
        expertSynthesis: dilemma.synthesis,
        documentationLinks: dilemma.documentation_links.map((l) =>
          l.replace(/^https?:\/\//i, ""),
        ),
        uploadedFiles: dilemma.uploaded_files,
      },
      reviewComment: dilemma.review_comment ?? "",
      isStep1Dirty: false,
      isStep2Dirty: false,
      isStep3Dirty: false,
    });
  },

  reset: () =>
    set({
      ...initialState,
      step2Data: {
        ...initialState.step2Data,
        answers: DEFAULT_ANSWERS.map((a) => ({ ...a })),
      },
    }),
}));
